// Ported from handover:
//   window.tasklet.sqlQuery → fetch('/api/sync-log')
//   window.tasklet.sendMessageToAgent → POST /api/run-sync

import React, { useState, useEffect } from 'react';
import { RefreshCw, CheckCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { SyncLogEntry } from '../types';
import { API_BASE } from '../config';

async function fetchSyncLog(): Promise<SyncLogEntry[]> {
  const res = await fetch(`${API_BASE}/api/sync-log`);
  if (!res.ok) throw new Error(`API error ${res.status}`);
  return res.json();
}

export const SyncLogTab: React.FC = () => {
  const [entries, setEntries]   = useState<SyncLogEntry[]>([]);
  const [loading, setLoading]   = useState(true);
  const [syncing, setSyncing]   = useState(false);
  const [syncMsg, setSyncMsg]   = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      setEntries(await fetchSyncLog());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleRunSync = async () => {
    setSyncing(true);
    setSyncMsg(null);
    try {
      const res = await fetch(`${API_BASE}/api/run-sync`, { method: 'POST' });
      const data = await res.json() as { started?: boolean; message?: string; error?: string };
      setSyncMsg(data.error ? `Error: ${data.error}` : 'Sync started — check your email in a few minutes.');
    } catch (e) {
      setSyncMsg(`Failed to trigger sync: ${e}`);
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="loading loading-spinner loading-lg text-primary"></span>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-semibold text-base">Sync History</h2>
        <div className="flex gap-2">
          <button className="btn btn-sm btn-ghost gap-1" onClick={load}>
            <RefreshCw size={14} /> Refresh
          </button>
          <button
            className="btn btn-sm btn-primary gap-1"
            onClick={handleRunSync}
            disabled={syncing}
          >
            {syncing ? <Loader2 size={14} className="animate-spin" /> : '▶'}
            Run Sync Now
          </button>
        </div>
      </div>

      {syncMsg && (
        <div className={`alert ${syncMsg.startsWith('Error') ? 'alert-error' : 'alert-success'} py-2`}>
          <span className="text-sm">{syncMsg}</span>
        </div>
      )}

      {entries.length === 0 ? (
        <div className="text-center py-12 text-base-content/40">
          No sync runs recorded yet. The first sync will run automatically next Monday.
        </div>
      ) : (
        <div className="space-y-3">
          {entries.map(entry => {
            const hasChanges = entry.bookings_added > 0 || entry.bookings_updated > 0 ||
                               entry.sheet_added > 0 || entry.sheet_updated > 0;
            const hasWarnings = !!entry.warnings;
            const isFailed = entry.status === 'error';
            return (
              <div key={entry.id} className="card bg-base-200 shadow-sm">
                <div className="card-body p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      {isFailed ? (
                        <AlertTriangle size={16} className="text-error shrink-0 mt-0.5" />
                      ) : hasWarnings ? (
                        <AlertTriangle size={16} className="text-warning shrink-0 mt-0.5" />
                      ) : (
                        <CheckCircle size={16} className="text-success shrink-0 mt-0.5" />
                      )}
                      <div>
                        <div className="font-semibold text-sm">{entry.run_date}</div>
                        <div className="text-xs text-base-content/50">
                          {entry.created_at}
                          {entry.duration_ms ? ` · ${(entry.duration_ms/1000).toFixed(1)}s` : ''}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap justify-end">
                      {isFailed && <span className="badge badge-error badge-sm">failed</span>}
                      {entry.bookings_added > 0 && (
                        <span className="badge badge-success badge-sm">+{entry.bookings_added} portal</span>
                      )}
                      {entry.bookings_updated > 0 && (
                        <span className="badge badge-info badge-sm">~{entry.bookings_updated} updated</span>
                      )}
                      {entry.sheet_added > 0 && (
                        <span className="badge badge-success badge-sm">+{entry.sheet_added} sheet</span>
                      )}
                      {entry.sheet_updated > 0 && (
                        <span className="badge badge-info badge-sm">~{entry.sheet_updated} sheet</span>
                      )}
                      {!hasChanges && !isFailed && (
                        <span className="badge badge-ghost badge-sm">No changes</span>
                      )}
                    </div>
                  </div>
                  {entry.details && (
                    <p className="text-xs text-base-content/70 mt-2 whitespace-pre-line line-clamp-3">
                      {entry.details}
                    </p>
                  )}
                  {entry.warnings && (
                    <div className="alert alert-warning py-2 px-3 mt-2">
                      <AlertTriangle size={12} />
                      <span className="text-xs whitespace-pre-line">{entry.warnings}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
